import { useEffect, useId, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCity } from "@fortawesome/free-solid-svg-icons"
import requests from "../api/requests"
import "./CityCombobox.css"

export const SEARCH_DEBOUNCE_MS = 300
export const MIN_QUERY_LENGTH = 2

export const cityLabel = (city, state) => (city && state ? `${city} — ${state}` : "")

/**
 * "Cidade — UF" field of the billing address when the CEP is not found
 * (spec asaas-pix D71): suggestions from GET /v1/municipios (prefix,
 * accents ignored), 300 ms after typing stops, from 2 characters. Choosing
 * one sets city + UF; typing again clears the choice, so only a city from
 * the list reaches the api (which answers invalid_city otherwise).
 */
export default function CityCombobox({ value, onChange, error }) {
  const listId = useId()
  const [query, setQuery] = useState(cityLabel(value?.city, value?.state))
  const [options, setOptions] = useState([])
  const [open, setOpen] = useState(false)
  const [searched, setSearched] = useState(false)
  const [active, setActive] = useState(-1)
  // Answers for an earlier query arrive late sometimes: only the last counts.
  const lastSearch = useRef(0)
  const timer = useRef(null)

  useEffect(() => () => clearTimeout(timer.current), [])

  const search = (text) => {
    clearTimeout(timer.current)
    const trimmed = text.trim()
    if (trimmed.length < MIN_QUERY_LENGTH) {
      lastSearch.current += 1
      setOptions([])
      setSearched(false)
      setOpen(false)
      return
    }
    timer.current = setTimeout(async () => {
      const id = ++lastSearch.current
      try {
        const res = await requests.searchMunicipios(trimmed)
        if (id !== lastSearch.current) return
        setOptions(Array.isArray(res?.data) ? res.data : [])
      } catch {
        if (id !== lastSearch.current) return
        setOptions([])
      }
      setSearched(true)
      setActive(-1)
      setOpen(true)
    }, SEARCH_DEBOUNCE_MS)
  }

  const handleInput = (e) => {
    setQuery(e.target.value)
    if (value) onChange(null)
    search(e.target.value)
  }

  const choose = (option) => {
    clearTimeout(timer.current)
    lastSearch.current += 1
    setQuery(cityLabel(option.name, option.uf))
    setOpen(false)
    setOptions([])
    onChange({ city: option.name, state: option.uf })
  }

  const handleKeyDown = (e) => {
    if (!open || options.length === 0) {
      if (e.key === "Escape") setOpen(false)
      return
    }
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActive((i) => (i + 1) % options.length)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActive((i) => (i <= 0 ? options.length - 1 : i - 1))
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault()
      choose(options[active])
    } else if (e.key === "Escape") {
      setOpen(false)
    }
  }

  const optionId = (i) => `${listId}-option-${i}`

  return (
    <div className="form-field cityCombobox">
      <div className="input-container">
        <FontAwesomeIcon icon={faCity} className="cityCombobox__icon" />
        <input
          id="city"
          name="city"
          role="combobox"
          aria-label="Cidade — UF"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
          aria-invalid={Boolean(error)}
          autoComplete="off"
          placeholder="Digite o nome da cidade"
          value={query}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          onBlur={() => setOpen(false)}
        />
      </div>
      {open && (
        <ul id={listId} role="listbox" className="cityCombobox__list">
          {options.map((option, i) => (
            <li
              key={`${option.name}-${option.uf}`}
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              className={`cityCombobox__option${i === active ? " cityCombobox__option--active" : ""}`}
              // mousedown keeps the input focused (blur would close the list first)
              onMouseDown={(e) => {
                e.preventDefault()
                choose(option)
              }}
            >
              {cityLabel(option.name, option.uf)}
            </li>
          ))}
          {searched && options.length === 0 && (
            <li className="cityCombobox__empty" role="option" aria-disabled="true" aria-selected={false}>
              Nenhuma cidade encontrada.
            </li>
          )}
        </ul>
      )}
      {error && <span className="cityCombobox__error">{error}</span>}
    </div>
  )
}
